import { InfiniteData, QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { PaginatedData } from '../@types';
import { getData, hasApiMorePages, type ApiService } from '../utils/api';
import useInfiniteScrollTrigger from './useInfiniteScrollTrigger';

export type InfiniteScrollOptions<T> = Omit<
  UseInfiniteQueryOptions<PaginatedData<T>, Error, InfiniteData<PaginatedData<T>, number>, QueryKey, number>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
>;

type Props<T> = InfiniteScrollOptions<T> & {
  queryKey: QueryKey;
  endpoint: string;
  queryParams?: {
    page?: number;
    limit?: number;
    [key: string]: unknown;
  };
  apiService?: ApiService;
  customHeaders?: Record<string, string>;
  totalCount?: number;
};

const useInfiniteScroll = <T = unknown>({
  queryKey,
  endpoint,
  queryParams,
  apiService = 'feed',
  customHeaders,
  totalCount,
  ...options
}: Props<T>) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isPending: isLoading,
    isFetching,
    isPlaceholderData,
    isError,
    isSuccess,
    isFetchingNextPage,
  } = useInfiniteQuery<PaginatedData<T>, Error, InfiniteData<PaginatedData<T>, number>, QueryKey, number>({
    queryKey: [...queryKey, queryParams],
    queryFn: ({ pageParam = 1 }): Promise<PaginatedData<T>> =>
      getData(
        endpoint,
        {
          page: pageParam,
          limit: queryParams?.limit ?? 5,
          ...queryParams,
        },
        apiService,
        customHeaders
      ),
    getNextPageParam: (lastPage) => {
      const count = totalCount ?? lastPage.count;

      if (count !== undefined && hasApiMorePages(count, lastPage.page, lastPage.limit)) {
        return lastPage.page + 1;
      }

      return undefined;
    },
    initialPageParam: 1,
    ...options,
  });

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { lastElementRef } = useInfiniteScrollTrigger({
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    onLoadMore: loadMore,
  });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const topMarketTypes = useMemo(() => data?.pages[0]?.topMarketTypes, [data]);

  return {
    data,
    items,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    isFetchingNextPage,
    lastElementRef,
    isInitialLoading: isLoading,
    hasNextPage,
    topMarketTypes,
    loadMore,
    isPreviousData: isPlaceholderData,
  };
};

export default useInfiniteScroll;
